import React from "react";
import { useQuery } from "@tanstack/react-query";
import { User, Shield, ShieldCheck, Mail, Calendar } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import "./UsersList.css";

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: "OWNER" | "OPERATOR";
  createdAt: string;
}

const UsersList: React.FC = () => {
  const { userData } = useUser();

  // React Query for users list
  const { data: users = [], isLoading: loading, error: errorObj, refetch } = useQuery<UserItem[]>({
    queryKey: ['coworkingUsers'],
    queryFn: async () => {
      const response = await api.get("/user/list", {
        headers: {
          Authorization: `Bearer ${userData?.auth}`,
        },
      });
      const data = (response as any)?.data ?? response;
      return Array.isArray(data) ? data : [];
    },
    enabled: !!userData?.auth,
  });

  const error = errorObj ? "Não foi possível carregar a lista de usuários." : null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getRoleLabel = (role: string) => {
    return role === "OWNER" ? "Proprietário" : "Operador";
  };

  const getRoleIcon = (role: string) => {
    return role === "OWNER" ? (
      <ShieldCheck size={16} className="role-icon owner" />
    ) : (
      <Shield size={16} className="role-icon operator" />
    );
  };

  if (loading) {
    return (
      <div className="users-list-loading">
        <div className="loading-spinner"></div>
        <p>Carregando usuários...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-list-error">
        <p>{error}</p>
        <button onClick={() => refetch()} className="retry-button">
          Tentar novamente
        </button>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="users-list-empty">
        <User size={48} className="empty-icon" />
        <p>Nenhum usuário encontrado.</p>
      </div>
    );
  }

  return (
    <div className="users-list">
      <div className="users-list-header">
        <span className="header-name">Usuário</span>
        <span className="header-role">Tipo</span>
        <span className="header-date">Criado em</span>
      </div>
      
      {users.map((user) => (
        <div key={user.id} className="user-item">
          <div className="user-info">
            <div className="user-avatar">
              {user.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="user-details">
              <span className="user-name">{user.name}</span>
              <span className="user-email">
                <Mail size={12} />
                {user.email}
              </span>
            </div>
          </div>
          
          <div className={`user-role ${user.role?.toLowerCase() || 'owner'}`}>
            {getRoleIcon(user.role || 'OWNER')}
            <span>{getRoleLabel(user.role || 'OWNER')}</span>
          </div>
          
          <div className="user-date">
            <Calendar size={12} />
            <span>{formatDate(user.createdAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UsersList;

